<template>
  <div class="deploy-vars">
    <notifications position="top center" />

    <div class="rounded-0 card">
      <div
        class="d-flex justify-content-between align-items-center card-header"
      >
        <h3 class="mb-0 text-left">Deploy variables</h3>
        <button
          class="btn btn-primary"
          @click="openCreatePopup"
          v-if="currentUser.role === 'SuperAdmin'"
        >
          <i class="text-light fa fa-plus"></i>
          Create
        </button>
      </div>
      <div class="card-body vld-parent">
        <loading
          :active.sync="loading"
          :can-cancel="false"
          color="#2196f3"
          :z-index="9"
        ></loading>

        <div class="vars">
          <div class="row var" v-for="(item, idx) in vars" :key="item.id">
            <div class="col-4">
              <b-form-group label="Key">
                <b-form-input v-model="vars[idx].key" type="text" disabled>
                </b-form-input>
              </b-form-group>
            </div>
            <div class="col-3">
              <b-form-group label="Name">
                <b-form-input
                  v-model="vars[idx].name"
                  type="text"
                  :disabled="item.disabled"
                >
                </b-form-input>
              </b-form-group>
            </div>
            <div class="col-3">
              <b-form-group label="Status">
                <b-form-select
                  v-model="vars[idx].status"
                  :options="statuses"
                  :disabled="item.disabled"
                >
                </b-form-select>
              </b-form-group>
            </div>
            <div
              class="text-center align-self-center col-2"
              v-if="currentUser.role === 'SuperAdmin'"
            >
              <button
                v-if="item.disabled"
                @click.prevent="enableFields(idx)"
                class="mr-4 p-1 btn"
              >
                <i class="flaticon-edit"></i>
              </button>
              <button
                v-if="!item.disabled"
                @click.prevent="editVar(item, idx)"
                class="mr-4 btn btn-primary"
              >
                Save
              </button>
              <button
                @click.prevent="openDeletePopup(idx)"
                class="mr-2 p-1 btn"
              >
                <i class="text-danger flaticon-delete"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- modals -->
    <modal
      name="deployDeletePopup"
      :width="500"
      height="auto"
      @closed="clearData"
    >
      <div class="pt-10 pb-5 container vld-parent">
        <h1 class="mb-5 text-center color-primary">
          Delete Deploy Variable "{{ currentVar.key }}"
        </h1>
        <form
          class="text-center delete-banner__form"
          @submit.prevent="deleteHandler"
          id="delete-banner"
        >
          <p>Are you sure you want to delete this Variable?</p>
          <div class="justify-content-center mt-5 row">
            <button class="rounded w-20 btn btn-danger">Delete</button>
          </div>
        </form>
      </div>
    </modal>

    <modal
      name="deployCreatePopup"
      :width="500"
      height="auto"
      @closed="clearData"
    >
      <div class="pt-10 pb-5 container vld-parent">
        <h2 class="text-center">Create Deploy Variable</h2>
        <form>
          <b-form-group label="Key">
            <b-form-input required v-model="form.key" type="text">
            </b-form-input>
            <span v-if="error.key" class="text-danger small">{{
              error.key
            }}</span>
          </b-form-group>
          <b-form-group label="Name">
            <b-form-input required v-model="form.name" type="text">
            </b-form-input>
            <span v-if="error.name" class="text-danger small">{{
              error.name
            }}</span>
          </b-form-group>
          <b-form-group label="Status">
            <b-form-select v-model="form.status" :options="statuses">
              <template v-slot:first>
                <b-form-select-option value="">
                  Select Status
                </b-form-select-option>
              </template>
            </b-form-select>
            <span v-if="error.status" class="text-danger small">{{
              error.status
            }}</span>
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
              id="must_include_in_response"
              v-model="form.must_include_in_response"
              name="must_include_in_response"
            >
              must include in response
            </b-form-checkbox>
            <span
              v-if="error.must_include_in_response"
              class="text-danger small"
              >{{ error.must_include_in_response }}</span
            >
          </b-form-group>

          <div class="text-center">
            <button
              @click.prevent="createHandler"
              class="font-weight-bold btn btn-primary"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </modal>
  </div>
</template>

<script>
import http from "@/axios";
import { mapGetters } from "vuex";

export default {
  data: () => ({
    loading: false,
    vars: [],
    statuses: [
      { text: "Activated", value: 1 },
      { text: "Deactivated", value: 0 },
    ],
    currentVar: {},
    form: {
      status: "",
      must_include_in_response: false,
    },
    error: {},
  }),
  async mounted() {
    await this.fetchVars();
  },
  methods: {
    // handlers
    async fetchVars() {
      this.loading = true;
      const res = await http.get("/sbc/variables/deploy");
      this.vars = res.data.data.map((item) => ({
        disabled: true,
        ...item,
      }));
      this.loading = false;
    },
    async createHandler() {
      try {
        this.loading = true;
        const res = await http.post("/sbc/variables", {
          deploy: 1,
          match: 0,
          must_include_in_response: true,
          type: "string",
          ...this.form,
        });
        this.$modal.hide("deployCreatePopup");
        this.loading = false;
        if (res.data.success) {
          this.fetchVars();
        }
      } catch (error) {
        this.loading = false;
        if (error.response.data.error) {
          this.error = error.response.data.error;
        } else {
          this.$notify({
            title: error.response.data.message,
            type: "error",
          });
        }
      }
    },
    async editVar(item, idx) {
      try {
        this.loading = true;
        const res = await http.put(`/sbc/variables/${item.id}`, item);
        this.loading = false;
        this.vars[idx].disabled = true;
      } catch (error) {
        this.loading = false;
        if (error.response.data.error) {
          this.error = error.response.data.error;
        } else {
          this.$notify({
            title: error.response.data.message,
            type: "error",
          });
        }
      }
    },
    async deleteHandler() {
      try {
        this.loading = true;
        const res = await http.delete(`/sbc/variables/${this.currentVar.id}`);
        this.$modal.hide("deployDeletePopup");
        this.loading = false;
        if (res.data.success) {
          this.fetchVars();
        }
      } catch (error) {
        this.$modal.hide("deployDeletePopup");
        this.loading = false;
        if (error.response.data.error) {
          this.error = error.response.data.error;
        } else {
          this.$notify({
            title: error.response.data.message,
            type: "error",
          });
        }
      }
    },
    // helpers
    enableFields(idx) {
      console.log(idx, this.vars[idx]);
      this.vars[idx].disabled = false;
      this.$forceUpdate();
    },
    // modals
    openDeletePopup(idx) {
      this.currentVar = this.vars[idx];
      this.$modal.show("deployDeletePopup");
    },
    openCreatePopup() {
      this.$modal.show("deployCreatePopup");
    },
    clearData() {
      this.form = {
        status: "",
        must_include_in_response: false,
      };
      this.error = {};
    },
  },
  computed: {
    ...mapGetters(["currentUser"]),
  },
};
</script>

<style></style>
